import _ from 'underscore'
import makeRpcCall from '../jquery_rpc'
import RpcError from './RpcError'
import {batched, batchedPromise} from '../../redux/batch'

// global storage of current rpc calls
let rpcCalls = {}

/**
 *
 * Perform rpc call and return promise function
 *
 * @param method
 * @param params
 * @param customHandler handler if need to prevent default error handler
 * @param abortKey string key
 *                 used to identify running rpcCall, by this key you can abort request
 *                 if key is null - request can not be aborted
 *                 default value equals name of requested method
 * @returns {Promise}
 */
export const rpcCall = (method, params, customHandler = (error) => null, abortKey = method) => {
    if (abortKey)
        abortCall(abortKey)

    return new Promise((resolve, reject) => {
        const call = makeRpcCall(
            method,
            params,
            (data) => {
                if (abortKey)
                    _removeCall(abortKey)
                resolve(data)
            },
            (jqXHR, textStatus, errorThrown) => {
                if (abortKey)
                    _removeCall(abortKey)

                // create error
                const error = new RpcError(jqXHR, textStatus, errorThrown)
                // TODO: get rid of legacy custome handler behavior - don't rely on it returning true/false
                const isHandled_legacy = customHandler(error);
                if (error.isHandled()) {
                    reject(error.getNewError() !== null ? error.getNewError() : error)
                    return true
                } else {
                    reject(error)
                    return isHandled_legacy
                }
            })

        if (abortKey)
            _addCall(abortKey, call)
    })
}

const _addCall = (abortKey, call) => {
    rpcCalls[abortKey] = call
}

const _removeCall = (abortKey) => {
    rpcCalls = _.omit(rpcCalls, abortKey)
}

const LONG_TASK_POLL_INTERVAL = 10000
/**
 * Wraps potentially long RPC call, so if it ends with timeout
 * then this function does not return error immediately. Instead
 * it periodically polls server for response of call made (not repeating same call, just
 * waiting for response)
 *
 * That is done via  'LongTaskService'
 *
 * TLDR: if your 'method' is fast, use 'rpcCall', if it may be slow
 * and may do longer than 60 seconds - use 'longRpcCall'
 */
export const longRpcCall = (method, params, customHandler = (error) => null, abortKey = method) => {
    let longTaskId = crypto.randomUUID()

    function pollForResult(originalTimeoutError) {
        return new Promise((resolve, reject) => {
            let doPoll = () => setTimeout(() => {
                rpcCall("LongTaskService.getStatus", [longTaskId])
                    .then(({registered, finished, result}) => {
                        if (!registered) {
                            reject(originalTimeoutError)  //timeout
                        } else if (finished) {
                            resolve(result.result)
                        } else {
                            doPoll()
                        }
                    })
                    .catch(error => {
                        reject(error)
                    })
            }, LONG_TASK_POLL_INTERVAL)
            doPoll()
        })
    }

    function isTimeout(error) {
        return error.getTextStatus() === "timeout" || error.getJQXHR().status === 504
    }

    function errorHandler(error) {
        if (isTimeout(error)) {
            //prevent default processing
            error.handle()
        } else {
            return customHandler(error)
        }
    }

    return rpcCall("LongTaskService.longTask", [longTaskId, method, params], errorHandler, abortKey)
        .then(result => {
            rpcCall("LongTaskService.resultReceived", [longTaskId])
            return result
        }).catch(e => {
            if (isTimeout(e) && e.isHandled()) {
                return pollForResult(e)
            } else {
                throw e;
            }
        })
}

/**
 * Abort rpc call by key
 * @param abortKey
 */
export const abortCall = (abortKey) => {
    const call = rpcCalls[abortKey]
    if (call) {
        call.abort()
        _removeCall(abortKey)
    }
}

/**
 * Version of 'rpcCall' with enabled redux batching.
 * Use it instead of standard 'rpcCall' if you want to enable batching in result promise.
 * @returns {BatchPromiseWrapper}
 */
export function batchedRpcCall(method, params, customHandler = () => null, abortKey) {
    return batchedPromise(rpcCall(method, params, batched(customHandler), abortKey))
}

export default rpcCall



// WEBPACK FOOTER //
// ./web/js/util/rpc/rpcCalls.js